// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { CachedBundleMembersDeltaListProperty } from '@/audit/api/types';
import { Stack, Typography } from '@mui/material';

interface Props {
  /**
   * The cached bundle members delta list property containing added/removed/updated members
   */
  property: CachedBundleMembersDeltaListProperty;
}

/**
 * Component for displaying bundle member delta list changes in audit logs.
 * Shows added, removed, and updated bundle members with their quantities and item details.
 *
 * Follows the same pattern as other delta list components like the BOM delta list.
 */
export function AuditCachedBundleMembersDeltaListLabel(props: Props) {
  const added = props.property.newValue?.added || [];
  const removed = props.property.newValue?.removed || [];
  const updated = props.property.newValue?.updated || [];

  // Don't render if all arrays are empty
  if (added.length === 0 && removed.length === 0 && updated.length === 0) {
    return null;
  }

  return (
    <Stack>
      <Typography component="span" variant="caption" fontWeight="bold">
        {props.property.name}:
      </Typography>

      {added.length > 0 && (
        <Stack sx={{ marginLeft: 2 }}>
          <Typography component="span" variant="caption" fontWeight="bold">
            Added:
          </Typography>
          {added.map((member) => (
            <Typography
              key={member.key}
              variant="caption"
              sx={{ marginLeft: 2 }}
            >
              {member.sku ? `${member.sku} (${member.name})` : member.name} -
              Quantity: {member.qty}
            </Typography>
          ))}
        </Stack>
      )}

      {removed.length > 0 && (
        <Stack sx={{ marginLeft: 2 }}>
          <Typography component="span" variant="caption" fontWeight="bold">
            Removed:
          </Typography>
          {removed.map((member) => (
            <Typography
              key={member.key}
              variant="caption"
              sx={{ marginLeft: 2 }}
            >
              {member.sku ? `${member.sku} (${member.name})` : member.name} -
              Quantity: {member.qty}
            </Typography>
          ))}
        </Stack>
      )}

      {updated.length > 0 && (
        <Stack sx={{ marginLeft: 2 }}>
          <Typography component="span" variant="caption" fontWeight="bold">
            Updated:
          </Typography>
          {updated.map((updateMember) => {
            const label = updateMember.new.sku
              ? `${updateMember.new.sku} (${updateMember.new.name})`
              : updateMember.new.name;
            // A save can change quantity, position, or both; render every
            // part that changed so a combined edit isn't misrepresented as a
            // quantity-only change
            const detailParts: string[] = [];
            if (updateMember.old.qty !== updateMember.new.qty) {
              detailParts.push(
                `Quantity: ${updateMember.old.qty} → ${updateMember.new.qty}`
              );
            }
            if (updateMember.old.position !== updateMember.new.position) {
              detailParts.push(
                `Moved from position ${updateMember.old.position + 1} to ${updateMember.new.position + 1}`
              );
            }
            const detail = detailParts.join(', ');
            return (
              <Typography
                key={`updated-${updateMember.new.id}`}
                variant="caption"
                sx={{ marginLeft: 2 }}
              >
                {detail ? `${label} - ${detail}` : label}
              </Typography>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}
